import { useEffect, useRef, useState, useCallback } from 'react'
import { HocuspocusProvider } from '@hocuspocus/provider'
import classNames from 'classnames'
import { useSetRecoilState } from 'recoil'
import * as Y from 'yjs'

import { AVATAR_COLORS } from 'consts/dashboardAvatarColors'
import { useAuth } from 'contexts/AuthContext'
import { useDebounce } from 'hooks'
import { dashboardTheme } from 'recoil/atoms'
import { UserStore } from 'stores'
import analyticsTracker from 'utils/analyticsTracker'
import getEnvFromLocation from 'utils/getEnvFromLocation'
import { DEFAULT_JOB_THEME } from 'utils/jobThemeUtils'

import { RealTimeContext } from './context'
import './index.scss'
import { DashBuilderStatus } from './types'

const socketProviderURLs = {
  devHocuspocus: 'wss://yjs-hocuspocus.dev.insights.winnin.com',
  prodHocuspocus: 'wss://yjs-hocuspocus.insights.winnin.com',
}

/**
 * Without a message for this long the connection is treated as dead. The server echoes every awareness update, so the
 * heartbeat below keeps a healthy but idle connection well inside this window.
 */
const CONNECTION_TIMEOUT_MS = 12_000
const HEARTBEAT_INTERVAL_MS = 5_000

const closeProvider = provider => {
  provider?.disconnect()
  provider?.destroy()
  provider?.configuration?.websocketProvider?.disconnect()
  provider?.configuration?.websocketProvider?.destroy()
}

function ContentWrapper({ isSynced, contentRef, children }) {
  return (
    <div
      ref={contentRef}
      data-testid="realtime-content"
      className={classNames({ 'RealTimeContext-noInteract': isSynced === false })}
    >
      {children}
    </div>
  )
}

export function RealTimeContextProvider({ children, dashboardId }) {
  const [isHocusPocusConnected, setIsHocusPocusConnected] = useState(false)
  const [isHocusPocusAuthenticated, setIsHocusPocusAuthenticated] = useState(false)
  const [connectedUsers, setConnectedUsers] = useState([])
  const [dashBuilderStatus, setDashBuilderStatus] = useState(DashBuilderStatus.Default)
  const [mustReload, setMustReload] = useState(false)

  const setDashboardTheme = useSetRecoilState(dashboardTheme)

  const yDocRef = useRef(null)
  const dashMapRef = useRef(null)
  const dashMapSectionsRef = useRef(null)
  const wsProvider = useRef(null)
  const undoManagerComponents = useRef(null)
  const contentRef = useRef<HTMLDivElement>(null)
  /** Mirrors the provider's sync state so disconnect handlers know whether the drop happened mid-session. */
  const isSyncedRef = useRef(false)
  /** Writes before the first sync are the initial load, not an interrupted session. */
  const hasEverSyncedRef = useRef(false)
  const mustReloadRef = useRef(false)

  const isSynced = isHocusPocusConnected && isHocusPocusAuthenticated && !mustReload

  /**
   * The server rejects a reconnect that carries writes it never received, and the client never recovers from that
   * rejection. A document holding such writes therefore stops reconnecting and stays blocked until the page reloads
   * with an empty document.
   */
  const markDirty = () => {
    mustReloadRef.current = true
    setMustReload(true)
    wsProvider.current?.configuration?.websocketProvider?.disconnect()
  }

  const auth = useAuth()

  useEffect(() => {
    const yDoc = new Y.Doc()
    yDocRef.current = yDoc

    const yDashboardThemeMap = yDocRef.current?.getMap('jobDashboardTheme')

    function handleThemeChange(event) {
      const yTargetMap = event.target

      if (!event.transaction.local || event.transaction.origin instanceof Y.UndoManager) {
        const updatedTheme = [...yTargetMap.values()][0]
        if (updatedTheme) {
          setDashboardTheme(updatedTheme)
        }
      }
    }

    yDashboardThemeMap?.observe(handleThemeChange)

    dashMapRef.current = yDoc.getMap('dashboard')
    dashMapSectionsRef.current = yDoc.getMap('sections')

    /** A local write while the session is interrupted can never reach the server. */
    yDoc.on('update', (_update, origin) => {
      const provider = wsProvider.current
      const isRemoteUpdate = origin === provider
      const isSessionInterrupted = hasEverSyncedRef.current && !isSyncedRef.current
      if (!isRemoteUpdate && isSessionInterrupted) markDirty()
    })

    const dashboardHeadersYmap = yDocRef.current?.getMap('dashboardHeaders')

    dashboardHeadersYmap.observe(event => {
      const value = event.target.toJSON()
      setDashBuilderStatus(value?.dashBuilderStatus)
    })

    undoManagerComponents.current = new Y.UndoManager([dashMapRef.current], {
      captureTimeout: 1000,
    })

    // can be uncommented for debug purposes
    // undoManagerComponents.current.on('stack-item-added', (event) => {
    //   // save the current cursor location on the stack-item
    //   // event.stackItem.meta.set('cursor-location', getRelativeCursorLocation())
    //   console.log('undoManager stack-item-added', event)
    // })
    // undoManagerComponents.current.on('stack-item-popped', (event) => {
    //   // restore the current cursor location on the stack-item
    //   // restoreCursorLocation(event.stackItem.meta.get('cursor-location'))
    //   console.log('undoManager stack-item-popped', event)
    // })

    initializeWebSockets(dashboardId, yDocRef, setIsHocusPocusConnected)

    return () => {
      closeProvider(wsProvider.current)
      setDashboardTheme(DEFAULT_JOB_THEME)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId])

  /**
   * Every write made while disconnected is pending at reconnect, and the server rejects a reconnect that carries
   * pending writes. So a lost connection must block editing as soon as any signal reveals it, not only when the
   * socket closes cleanly.
   */
  useEffect(function blockEditingAsSoonAsConnectionIsLost() {
    const markDisconnected = () => {
      setIsHocusPocusConnected(false)
      wsProvider.current?.configuration?.websocketProvider?.webSocket?.close()
    }

    const handleOffline = () => markDisconnected()
    const handleOnline = () => {
      if (!mustReloadRef.current) wsProvider.current?.connect()
    }

    /** A tab coming back from the background may hold a connection that died while its timers were throttled. */
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      const lastMessageReceived = wsProvider.current?.configuration?.websocketProvider?.lastMessageReceived
      if (lastMessageReceived && Date.now() - lastMessageReceived > CONNECTION_TIMEOUT_MS) {
        markDisconnected()
      }
    }

    /** Re-publishing the unchanged local state broadcasts it without firing awareness `change` for other clients. */
    const heartbeat = window.setInterval(() => {
      const awareness = wsProvider.current?.awareness
      const localState = awareness?.getLocalState()
      if (localState) awareness.setLocalState(localState)
    }, HEARTBEAT_INTERVAL_MS)

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(heartbeat)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  /** The overlay only stops the mouse; a focused editor would keep writing keystrokes into the document. */
  useEffect(
    function releaseFocusWhenDisconnected() {
      if (isSynced) return
      const activeElement = document.activeElement
      if (activeElement instanceof HTMLElement && contentRef.current?.contains(activeElement)) {
        activeElement.blur()
      }
    },
    [isSynced]
  )

  useEffect(() => {
    const handleKeyDown = event => {
      if (!wsProvider.current?.synced) return

      const code = event.which || event.keyCode
      const charCode = String.fromCharCode(code).toLowerCase()
      const regularCtrl = event.ctrlKey || event.metaKey
      const ctrlShift = regularCtrl && event.shiftKey
      const isCtrlPressed = regularCtrl || ctrlShift

      if (isCtrlPressed && charCode === 'z') {
        undoManagerComponents.current.undo()
      } else if (isCtrlPressed && charCode === 'y') {
        undoManagerComponents.current.redo()
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  function initializeWebSockets(dashboardId, ydocRef, setIsHocusPocusConnected) {
    const env = getEnvFromLocation()
    const isProduction = ['staging', 'production'].includes(env)

    setIsHocusPocusConnected(false)
    closeProvider(wsProvider.current)
    isSyncedRef.current = false
    hasEverSyncedRef.current = false
    mustReloadRef.current = false
    setMustReload(false)

    /** A replaced provider keeps emitting events while it tears down; only the current one may drive state. */
    const isStale = () => wsProvider.current !== provider

    /**
     * Writes the server had not acknowledged when a live session dropped are still pending: they were lost in flight
     * or never sent. The provider's own sync handshake also counts as unsynced, so only a drop mid-session counts.
     */
    const handleConnectionLost = () => {
      if (isSyncedRef.current && provider.unsyncedChanges > 0) markDirty()
      isSyncedRef.current = false
      /**
       * A socket that cannot even close (no network) is force-closed without a `close` event, which leaves the
       * provider flagged as synced; the next sync would then not emit `synced` and the overlay would never lift.
       */
      provider.synced = false
      setIsHocusPocusConnected(false)
    }

    const provider = new HocuspocusProvider({
      url: isProduction ? socketProviderURLs.prodHocuspocus : socketProviderURLs.devHocuspocus,
      name: dashboardId,
      document: ydocRef.current,
      /**
       * Called on every (re)connection, before the sync handshake: an expired session token never reaches the server,
       * and a document that must reload never gets to sync its pending writes, even from a retry already scheduled.
       */
      token: async () => {
        if (mustReloadRef.current) {
          provider.configuration.websocketProvider.disconnect()
          throw new Error('Document has writes the server rejects; it must reload')
        }
        await auth?.keycloak?.updateToken(60)
        return auth?.keycloak?.token
      },
      onAuthenticated: () => {
        console.log('authenticated')
        setIsHocusPocusAuthenticated(true)
      },
      onAuthenticationFailed: ({ reason }) => {
        if (isStale()) return
        console.log('authentication failed', reason)
        setIsHocusPocusAuthenticated(false)
      },
      onSynced: ({ state }) => {
        if (isStale()) return
        console.log('sync', state)
        hasEverSyncedRef.current = hasEverSyncedRef.current || state
        isSyncedRef.current = state
        setIsHocusPocusConnected(state)
      },
      onConnect() {
        console.log('connect')
      },
      onDisconnect: ({ event }) => {
        if (isStale()) return
        console.log('disconnect', event)
        handleConnectionLost()
      },
      onClose({ event }) {
        if (isStale()) return
        console.log('close', event)
        if (event.reason === 'locked') {
          window.location.reload()
          return
        }

        /**
         * A socket drop arrives as a CloseEvent and the provider reconnects on its own. A plain object means the
         * server closed only this document, which it does when it rejects this client's state: it is not recoverable.
         */
        if (!(event instanceof CloseEvent)) {
          markDirty()
        }
        handleConnectionLost()
      },
      onAwarenessChange: () => {
        const awarenessStates = Array.from(wsProvider.current.awareness.getStates().values())
        let awarenessConnectedUsers = awarenessStates.map(state => (state as { user: any }).user)
        awarenessConnectedUsers = awarenessConnectedUsers.filter(
          (user, index, self) =>
            index ===
            self.findIndex(u => {
              return u?.id === user?.id
            })
        ) // deduplicate users by id (an user might be connected in several tabs or browsers)
        const usersWithColors = awarenessConnectedUsers.map((item, index) => {
          const modifier = Math.ceil(index / 8)
          const colorIndex = index > 8 ? 8 * modifier - index : index

          return {
            ...item,
            color: AVATAR_COLORS?.[colorIndex],
          }
        })
        setConnectedUsers(usersWithColors)
      },
    })
    provider.configuration.websocketProvider.setConfiguration({ messageReconnectTimeout: CONNECTION_TIMEOUT_MS })
    wsProvider.current = provider
    wsProvider.current.awareness.setLocalStateField('user', {
      id: UserStore.userInfo.id,
      name: UserStore.userInfo.name,
      thumbnail: UserStore.userInfo.thumbnail,
    })
  }

  const sendStateless = useCallback((operationType, operationPayload) => {
    const statelessMessage = JSON.stringify({
      operationType,
      operationPayload,
    })
    wsProvider.current.sendStateless(statelessMessage)
  }, [])

  /**
   *
   * @param {'undo' | 'redo'} action
   * @returns {void}
   */
  const handleUndoRedo = useCallback(
    action => {
      return () => {
        const { UNDO, REDO } = analyticsTracker.eventNames
        if (action === 'undo') {
          undoManagerComponents.current.undo()
          analyticsTracker.eventTrack(UNDO, {
            userId: UserStore.userInfo.id,
            dashboardId,
          })
          return
        }

        undoManagerComponents.current.redo()
        analyticsTracker.eventTrack(REDO, {
          userId: UserStore.userInfo.id,
          dashboardId,
        })
      }
    },
    [dashboardId]
  )

  const handleResetUndoManager = useCallback(() => {
    undoManagerComponents.current.clear()
  }, [])

  const updateDashboardTheme = useCallback(
    theme => {
      if (!yDocRef.current) return

      const yDashboardThemeMap = yDocRef.current.getMap('jobDashboardTheme')

      yDocRef.current.transact(() => {
        yDashboardThemeMap.set('jobDashboardTheme', theme)
      })

      setDashboardTheme(theme)
    },
    [setDashboardTheme]
  )

  const resetUser = () => {
    const baseUser = connectedUsers.find(item => item.id === UserStore.userInfo.id)

    wsProvider.current.awareness.setLocalStateField('user', {
      ...baseUser,
      subgridId: null,
    })
  }

  const { debouncedAction: debouncedresetUser } = useDebounce(resetUser)

  const updateUserState = state => {
    debouncedresetUser()

    const baseUser = connectedUsers.find(item => item.id === UserStore.userInfo.id)

    wsProvider.current.awareness.setLocalStateField('user', {
      ...baseUser,
      ...state,
    })
  }

  return (
    <RealTimeContext.Provider
      value={{
        yDocRef,
        isHocusPocusConnected,
        dashMapRef,
        dashMapSectionsRef,
        updateDashboardTheme,
        sendStateless,
        connectedUsers,
        handleUndoRedo,
        resetUndoManager: handleResetUndoManager,
        updateUserState,
        dashBuilderStatus,
      }}
    >
      <ContentWrapper isSynced={isSynced} contentRef={contentRef}>
        {children}
      </ContentWrapper>
    </RealTimeContext.Provider>
  )
}
